import { IFieldDef } from "@components/smart/FieldInfo";
import { withDisplayName } from "@components/smart/GeneralFieldDefinition";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    PrBenefitEntity,
    PrBenefitTemporalCurrentEntity,
    PrBenefitTemporalEntity,
} from "@odata/GeneratedEntityTypes";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { transformToODataString } from "@odata/OData.utils";
import {
    getTableTemporalAdditionalProperties,
    getTemporalAdditionalProperties,
    getTemporalDataColumns,
    getTemporalPropertyExtraFieldContentAfter,
    getTemporalPropertyFieldDefinition,
    TemporalEntityProp,
    TemporalValidityFieldsDef
} from "@odata/TemporalUtils";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "@pages/PageUtils";
import {
    BenefitFakeTypePath,
    BenefitStatus,
    BenefitVariant,
    getBenefitColumns,
    getBenefitFieldDefinition,
    getBenefitTemporalDialogSettings,
    getFilterForTabId,
    isMealVoucher
} from "@pages/payroll/benefits/Benefit.utils";
import BenefitFormView from "@pages/payroll/benefits/BenefitFormView";
import { getPayrollCurrencyTableFormatter } from "@pages/payroll/payroll.utils";
import i18next from "i18next";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { GroupedField, Sort, ValueType } from "../../../enums";
import { Model } from "../../../model/Model";
import { IFormDef } from "../../../views/formView/Form";
import { ISplitPageTableDef, ITableDefTabData, SECONDARY_FILTERS_ALL } from "../../../views/table/TableView.utils";

interface IGetDefinitionArgs {
    inEditableWindow?: boolean;
    selectedIds?: number[];
    filters?: string[];
}

export const getDefinitionsFactory: ((args?: IGetDefinitionArgs) => IGetDefinition) = (args) => {
    const { inEditableWindow, selectedIds, filters } = args ?? {};

    const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {

        const preventStoreVariant = inEditableWindow;

        const _filters: string[] = [...(filters ?? [])];
        if (selectedIds?.length) {
            _filters.push(`not(${PrBenefitEntity.Id} in (${transformToODataString(selectedIds, ValueType.Number)}))`);
        }

        if (inEditableWindow) {
            _filters.push(getFilterForTabId(BenefitStatus.Active));
        }

        const tableAdditionalProperties: IFieldDef[] = [];

        if (inEditableWindow) {
            const cols = getTemporalDataColumns(getBenefitColumns(BenefitVariant.Employment));

            cols.forEach(col => {
                tableAdditionalProperties.push({ id: createPath(PrBenefitEntity.CurrentTemporalPropertyBag, col) });
                tableAdditionalProperties.push({ id: createPath(PrBenefitEntity.TemporalPropertyBag, col) });
            });
        } else {
            tableAdditionalProperties.push(...getTableTemporalAdditionalProperties(PrBenefitEntity));
        }

        const defaultTabs = [
            { id: BenefitStatus.Active, title: i18next.t("Benefit:Status.Active") },
            { id: BenefitStatus.Finished, title: i18next.t("Benefit:Status.Finished") },
            { id: SECONDARY_FILTERS_ALL, title: i18next.t("Benefit:Status.All") },
        ];
        const tabs: ITableDefTabData[] = inEditableWindow ? null : defaultTabs;

        const table: ISplitPageTableDef = {
            id: `${EntityTypeName.PrBenefit}Table`,
            preventStoreVariant,
            filterBarDef: [{
                ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
                defaultFilters: [
                    createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.Name),
                    PrBenefitEntity.Type,
                ],
                filterDefinition: {
                    [createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.Name)]: {},
                    [createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.Amount)]: {
                        label: i18next.t("Benefit:Form.Amount"),
                        formatter: getPayrollCurrencyTableFormatter()
                    },
                    ...withDisplayName(PrBenefitEntity.Type),
                    ...withDisplayName(createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.OtherCategory)),
                    ...withDisplayName(createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.TaxDeducibility)),
                    ...withDisplayName(createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.TaxAndContributionsApplicability)),
                },
                isValueHelp: true
            }],
            filter: _filters.join(" AND "),
            initialSortBy: [{
                id: PrBenefitEntity.Type,
                sort: Sort.Asc
            }, {
                id: createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.Name),
                sort: Sort.Asc
            }],
            columns: [
                createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.Name),
                PrBenefitEntity.Type,
                // statusLocalPath,
                createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.Amount)
            ],
            columnDefinition: {
                [createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.Name)]: {},
                ...withDisplayName(PrBenefitEntity.Type),
                // [statusLocalPath]: {
                //     label: i18next.t("Benefit:Table.Status"),
                //     fieldSettings: {
                //         disableSort: true
                //     },
                //     formatter: (val: TValue, args?: IFormatOptions) => {
                //         const { item } = args;
                //         const dateValidFrom = item?.[PrBenefitEntity.CurrentTemporalPropertyBag]?.[PrBenefitTemporalCurrentEntity.DateValidFrom] as Date;
                //         const dateValidTo = item?.[PrBenefitEntity.CurrentTemporalPropertyBag]?.[PrBenefitTemporalCurrentEntity.DateValidTo] as Date;
                //         const today = getUtcDayjs();
                //         let status = BenefitStatus.Active;
                //         let severity: Status | null = Status.Success;
                //         if (dateValidFrom && today.isBefore(dateValidFrom)) {
                //             status = BenefitStatus.Planned;
                //             severity = Status.Warning;
                //         } else if (dateValidTo && today.isAfter(dateValidTo)) {
                //             status = BenefitStatus.Finished;
                //             severity = null;
                //         }
                //         const tooltip = i18next.t(["Single", ""].map(prefix => `Benefit:Status.${prefix}${status}`)).toString();
                //         const formattedValue: ICellValueObject = {
                //             value: (
                //                     <SemanticText severity={severity}>
                //                         {tooltip}
                //                     </SemanticText>
                //             ),
                //             tooltip
                //         };
                //         return formattedValue;
                //     },
                // },
                [createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.Amount)]: {
                    label: i18next.t("Benefit:Form.Amount"),
                    formatter: getPayrollCurrencyTableFormatter()
                },
                [createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.AmountEmployee)]: {
                    label: i18next.t("Benefit:Form.EmployeeAmount"),
                    formatter: getPayrollCurrencyTableFormatter()
                },
                [createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.AmountEmployer)]: {
                    label: i18next.t("Benefit:Form.EmployerAmount"),
                    formatter: getPayrollCurrencyTableFormatter()
                },
                ...withDisplayName(createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.OtherCategory)),
                ...withDisplayName(createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.TaxDeducibility)),
                ...withDisplayName(createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.TaxAndContributionsApplicability)),
            },
            additionalProperties: tableAdditionalProperties,
            title: i18next.t("Benefit:TableTitle"),
            tabs,
            tabsSettings: {
                customFilter: getFilterForTabId
            }
        };

        const summary: ISummaryItem[] = [];

        const temporalDialogSettings = getBenefitTemporalDialogSettings(BenefitVariant.Base, false);

        const form: IFormDef = {
            id: `${EntityTypeName.PrBenefit}Form`,
            getItemBreadCrumbText: (storage: Model) =>
                    getItemBreadCrumbsText(storage, i18next.t("Benefit:NewBenefit"), i18next.t("Benefit:Form.Title")),
            translationFiles: getDefinitions.translationFiles,
            isDeletable: true,
            formControl: BenefitFormView,
            summary,
            additionalProperties: [
                ...getTemporalAdditionalProperties(PrBenefitTemporalEntity, PrBenefitEntity),
                { id: PrBenefitEntity.TypeCode },
            ],
            fieldDefinition: {
                ...TemporalValidityFieldsDef,
                ...getTemporalPropertyFieldDefinition({
                    propName: PrBenefitTemporalEntity.Name,
                    definition: {
                        ...getBenefitFieldDefinition(PrBenefitTemporalEntity.Name, temporalDialogSettings),
                        groupedField: GroupedField.WrappedMultiStart,
                    },
                    withOpeningButton: false
                }),
                [PrBenefitEntity.Type]: {
                    ...getBenefitFieldDefinition(PrBenefitEntity.Type),
                    isReadOnly: true,
                    customizationData: {
                        useForCustomization: false
                    }
                },
                ...getTemporalPropertyFieldDefinition({
                    propName: PrBenefitTemporalEntity.Amount,
                    definition: {
                        ...getBenefitFieldDefinition(PrBenefitTemporalEntity.Amount, temporalDialogSettings),
                        groupedField: (args => isMealVoucher(args) ? null : GroupedField.WrappedMultiEnd)
                    },
                    currentDefinition: {
                        extraFieldContentAfter: (args) =>
                                isMealVoucher(args) ? null : getTemporalPropertyExtraFieldContentAfter()(args)
                    }
                }),
                ...getTemporalPropertyFieldDefinition({
                    propName: PrBenefitTemporalEntity.AmountEmployer,
                    definition: {
                        ...getBenefitFieldDefinition(PrBenefitTemporalEntity.AmountEmployer, temporalDialogSettings),
                    },
                    withOpeningButton: false
                }),
                ...getTemporalPropertyFieldDefinition({
                    propName: PrBenefitTemporalEntity.OtherCategory,
                    definition: {
                        ...getBenefitFieldDefinition(PrBenefitTemporalEntity.OtherCategory, temporalDialogSettings),
                    },
                    withOpeningButton: false
                }),
                ...getTemporalPropertyFieldDefinition({
                    propName: PrBenefitTemporalEntity.TaxDeducibility,
                    definition: {
                        ...getBenefitFieldDefinition(PrBenefitTemporalEntity.TaxDeducibility, temporalDialogSettings),
                    },
                    withOpeningButton: false
                }),
                ...getTemporalPropertyFieldDefinition({
                    propName: PrBenefitTemporalEntity.TaxAndContributionsApplicability,
                    definition: {
                        ...getBenefitFieldDefinition(PrBenefitTemporalEntity.TaxAndContributionsApplicability, temporalDialogSettings),
                    },
                    withOpeningButton: false
                }),
                ...getTemporalPropertyFieldDefinition({
                    propName: PrBenefitTemporalEntity.AmountEmployee,
                    definition: {
                        ...getBenefitFieldDefinition(PrBenefitTemporalEntity.AmountEmployee, temporalDialogSettings),
                        groupedField: GroupedField.WrappedMultiEnd
                    },
                    withOpeningButton: true
                }),
                [createPath(TemporalEntityProp.TemporalPropertyBag, BenefitFakeTypePath)]: {
                    ...getBenefitFieldDefinition(BenefitFakeTypePath)
                },
            },
            groups: [
                {
                    id: "main",
                    rows: [
                        [
                            { id: createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalEntity.Name) },
                            { id: PrBenefitEntity.Type },
                            { id: createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalEntity.OtherCategory) },
                            { id: createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalEntity.TaxAndContributionsApplicability) },
                            { id: createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalEntity.TaxDeducibility) },
                            { id: createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalEntity.Amount) },
                            { id: createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalEntity.AmountEmployer) },
                            { id: createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalEntity.AmountEmployee) }
                        ]
                    ]
                }
            ]
        };

        return {
            entitySet: EntitySetName.PrBenefits,
            table,
            form
        };
    };

    getDefinitions.translationFiles = [
        "Benefit",
        "Common",
        getEnumNameSpaceName(EntityTypeName.PrBenefitType),
    ];

    return getDefinitions;
};

export const getDefinitions = getDefinitionsFactory();

setDefByEntityType(EntityTypeName.PrBenefit, getDefinitions);